import { IconExternalLink, IconKey } from "@tabler/icons-react";
import type { MouseEvent, ReactNode } from "react";

export type AppSidebarItem = {
  title: string;
  url: string;
  icon: ReactNode;
  isExternal?: boolean;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
};

export type AppSidebarSection = {
  label: string;
  items: AppSidebarItem[];
};

export type LinkRendererProps = {
  href: string;
  className: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  children: ReactNode;
  "aria-label"?: string;
};

/**
 * Props passed to the image renderer.
 * `width` and `height` are the SVG's intrinsic pixel dimensions, used by
 * Next.js <Image> for aspect-ratio and CLS prevention. Display sizing is
 * always controlled by the CSS className.
 */
export type ImageRendererProps = {
  src: string;
  alt: string;
  className: string;
  /** SVG intrinsic width (px) — controls aspect ratio, not display size. */
  width: number;
  /** SVG intrinsic height (px) — controls aspect ratio, not display size. */
  height: number;
  "aria-hidden"?: true;
};

export type AppSidebarProps = {
  /** URL used to highlight the active nav item. */
  activeUrl: string;
  /** Nav sections to render. Build these from the app-sidebar-nav data in the consuming app. */
  sections: AppSidebarSection[];
  /** Allows injecting framework-specific link components (e.g. Next.js <Link>). Defaults to <a>. */
  renderLink?: (props: LinkRendererProps) => ReactNode;
  /**
   * Allows injecting framework-specific image components (e.g. Next.js <Image>). Defaults to <img>.
   * Docusaurus consumers should omit this (or use the default) since next/image is unavailable there.
   */
  renderImage?: (props: ImageRendererProps) => ReactNode;
  /** Slot rendered at the bottom of the sidebar (e.g. NavUser or Sign in link). */
  footerSlot: ReactNode;
  /**
   * Set to "dark" to force the dark-background logo variant.
   * Docs sidebar is always dark; main app uses "auto" (responds to .dark on <html>).
   */
  sidebarTheme?: "auto" | "dark";
  /**
   * Override the URL-based CTA active state detection.
   * When true, the CTA always renders with the active ring.
   * Useful for sidebar contexts (e.g. docs) where the CTA should be permanently prominent.
   */
  ctaActive?: boolean;
  /**
   * When true, the "Manage API keys" CTA is not rendered.
   * Used in docs to hide the CTA for unauthenticated visitors.
   */
  hideCta?: boolean;
  /**
   * When true, renders skeleton rows in place of the CTA and nav sections.
   * Used in docs while useRadiumAuth is resolving to prevent a flash from the
   * single-item unauthenticated state to the full authenticated nav.
   */
  navLoading?: boolean;
};

/**
 * Skeleton structure that mirrors the authenticated nav.
 * Single unlabeled section with the flat item count (main + developer + account + apps).
 */
const NAV_SKELETON_ITEM_COUNT = 8;

const defaultRenderLink = ({ href, className, onClick, children, ...rest }: LinkRendererProps): ReactNode => (
  <a href={href} className={className} onClick={onClick} {...rest}>
    {children}
  </a>
);

const defaultRenderImage = ({
  src,
  alt,
  className,
  "aria-hidden": ariaHidden,
  width: _w,
  height: _h,
}: ImageRendererProps): ReactNode => (
  // biome-ignore lint/performance/noImgElement: default fallback for consumers without next/image (e.g. Docusaurus); use the renderImage prop to inject <Image> in Next.js contexts
  <img src={src} alt={alt} className={className} aria-hidden={ariaHidden} />
);

export const AppSidebarShell = ({
  activeUrl,
  sections,
  renderLink = defaultRenderLink,
  renderImage = defaultRenderImage,
  footerSlot,
  sidebarTheme = "auto",
  ctaActive,
  hideCta = false,
  navLoading = false,
}: AppSidebarProps) => {
  return (
    <div className="ra-sidebar" data-theme-sidebar={sidebarTheme === "dark" ? "dark" : undefined}>
      {/* Header — logo */}
      <div className="ra-sidebar__header">
        {renderLink({
          href: "/",
          className: "ra-sidebar__logo-link",
          "aria-label": "Radium home",
          children: (
            <>
              {/* Wordmark: dark variant (light sidebar bg) and light variant (dark sidebar bg).
                  Dimensions are the SVG's intrinsic pixels; display size is CSS-controlled. */}
              {renderImage({
                src: "/brand/radium-logo-dark.svg",
                alt: "Radium",
                className: "ra-sidebar__logo-wordmark ra-sidebar__logo--on-light",
                width: 512,
                height: 679,
              })}
              {renderImage({
                src: "/brand/radium-logo-light.svg",
                alt: "",
                "aria-hidden": true,
                className: "ra-sidebar__logo-wordmark ra-sidebar__logo--on-dark",
                width: 512,
                height: 679,
              })}
              {/* Icon-only (collapsed state) */}
              {renderImage({
                src: "/brand/radium-icon-dark.svg",
                alt: "Radium",
                className: "ra-sidebar__logo-icon ra-sidebar__logo--on-light",
                width: 512,
                height: 512,
              })}
              {renderImage({
                src: "/brand/radium-icon-light.svg",
                alt: "",
                "aria-hidden": true,
                className: "ra-sidebar__logo-icon ra-sidebar__logo--on-dark",
                width: 512,
                height: 512,
              })}
            </>
          ),
        })}
      </div>

      {/* Content — CTA + nav sections (or skeleton while auth is loading) */}
      <div className="ra-sidebar__content">
        {navLoading ? (
          <div className="ra-sidebar__group">
            <ul className="ra-sidebar__menu">
              {Array.from({ length: NAV_SKELETON_ITEM_COUNT }).map((_, ii) => (
                <li key={ii} className="ra-sidebar__menu-item">
                  <div className="ra-sidebar-nav__skeleton-item" aria-hidden>
                    <div className="ra-sidebar-nav__skeleton-icon" />
                    <div className="ra-sidebar-nav__skeleton-text" />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <>
            {!hideCta && (
              <div className="ra-sidebar__cta-wrap">
                {renderLink({
                  href: "/deploy/keys",
                  className: `ra-sidebar__cta${ctaActive ? " ra-sidebar__cta--active" : ""}`,
                  children: (
                    <>
                      <IconKey className="ra-sidebar__menu-icon" aria-hidden />
                      <span className="ra-sidebar__menu-link-label">Manage API keys</span>
                    </>
                  ),
                })}
              </div>
            )}

            {sections.map((section, sectionIndex) => (
              <div key={section.label || `section-${sectionIndex}`} className="ra-sidebar__group">
                {section.label ? <div className="ra-sidebar__group-label">{section.label}</div> : null}
                <ul className="ra-sidebar__menu">
                  {section.items.map((item) => {
                    const itemBase = item.url.replace(/\/$/, "");
                    const isActive = activeUrl === item.url || activeUrl.startsWith(`${itemBase}/`);
                    return (
                      <li key={item.url} className="ra-sidebar__menu-item">
                        {renderLink({
                          href: item.url,
                          className: `ra-sidebar__menu-link${isActive ? " ra-sidebar__menu-link--active" : ""}`,
                          onClick: item.onClick,
                          children: (
                            <>
                              {item.icon}
                              <span className="ra-sidebar__menu-link-label">{item.title}</span>
                              {item.isExternal && (
                                <IconExternalLink className="ra-sidebar__external-icon" aria-hidden />
                              )}
                            </>
                          ),
                        })}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Footer slot */}
      {footerSlot != null && <div className="ra-sidebar__footer">{footerSlot}</div>}
    </div>
  );
};
